<template>
  <div class="relative">
    <!-- Search Input -->
    <div class="relative">
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <MagnifyingGlassIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        class="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 sm:text-sm transition-colors duration-200"
        placeholder="Search documentation..."
        @input="handleSearch"
        @focus="showResults = true"
        @blur="handleBlur"
        @keydown.esc="clearSearch"
        @keydown.enter="handleEnter"
        @keydown.arrow-down="navigateResults(1)"
        @keydown.arrow-up="navigateResults(-1)"
        :aria-label="'Search documentation'"
        :aria-expanded="showResults"
        :aria-controls="'search-results'"
        role="combobox"
        aria-autocomplete="list"
      />

      <!-- Clear Button -->
      <button
        v-if="searchQuery"
        @click="clearSearch"
        class="absolute inset-y-0 right-0 pr-3 flex items-center"
        type="button"
        aria-label="Clear search"
      >
        <XMarkIcon class="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors duration-200" />
      </button>
    </div>

    <!-- Search Results Dropdown -->
    <div
      v-if="showResults && (searchResults.length > 0 || isLoading)"
      id="search-results"
      class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto"
      role="listbox"
    >
      <!-- Loading State -->
      <div v-if="isLoading" class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        <div class="flex items-center">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400 mr-2"></div>
          Searching...
        </div>
      </div>

      <!-- Search Results -->
      <template v-else>
        <div
          v-if="searchResults.length === 0 && searchQuery"
          class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400"
        >
          No results found for "{{ searchQuery }}"
        </div>

        <template v-else>
          <div
            v-for="(result, index) in searchResults"
            :key="result.id"
            class="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors duration-200"
            :class="{ 'bg-primary-50 dark:bg-primary-900': index === selectedIndex }"
            @click="selectResult(result)"
            @mouseenter="selectedIndex = index"
            role="option"
            :aria-selected="index === selectedIndex"
          >
            <div class="flex items-start">
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2">
                  <span class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {{ result.title }}
                  </span>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  >
                    {{ result.category }}
                  </span>
                </div>

                <!-- Highlighted Content Preview -->
                <div class="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                  <span v-if="result.matches.title" class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                    {{ result.title }}
                  </span>
                  <span v-else>{{ result.title }}</span>
                  -
                  <span v-if="result.matches.content" class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                    {{ getContentPreview(result.content) }}
                  </span>
                  <span v-else>{{ getContentPreview(result.content) }}</span>
                </div>

                <!-- Tags -->
                <div v-if="result.tags.length > 0" class="mt-2 flex flex-wrap gap-1">
                  <span
                    v-for="tag in result.tags.slice(0, 3)"
                    :key="tag"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                  >
                    {{ tag }}
                  </span>
                  <span v-if="result.tags.length > 3" class="text-xs text-gray-500 dark:text-gray-400">
                    +{{ result.tags.length - 3 }} more
                  </span>
                </div>

                <!-- Last Updated -->
                <div class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Updated {{ formatDate(result.lastUpdated) }}
                </div>
              </div>

              <!-- Relevance Score -->
              <div class="ml-2 flex-shrink-0">
                <div class="flex items-center space-x-1">
                  <div
                    v-for="i in 3"
                    :key="i"
                    class="w-2 h-2 rounded-full"
                    :class="i <= getRelevanceScore(result.score) ? 'bg-primary-500 dark:bg-primary-400' : 'bg-gray-200 dark:bg-gray-600'"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, nextTick } from 'vue'
  import { useRouter } from 'vue-router'
  import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'
  import { useDocumentationStore } from '@/stores/documentation'
  import type { SearchResult } from '@/types/documentation'

  // Router
  const router = useRouter()

  // Store
  const documentationStore = useDocumentationStore()

  // Refs
  const searchInput = ref<HTMLInputElement>()
  const searchQuery = ref('')
  const showResults = ref(false)
  const selectedIndex = ref(-1)

  // Computed
  const searchResults = computed(() => documentationStore.searchResults)
  const isLoading = computed(() => documentationStore.isLoading)

  // Methods
  const handleSearch = () => {
    if (searchQuery.value.trim()) {
      documentationStore.searchDocumentation(searchQuery.value)
    } else {
      documentationStore.clearSearch()
    }
    selectedIndex.value = -1
  }

  const clearSearch = () => {
    searchQuery.value = ''
    documentationStore.clearSearch()
    showResults.value = false
    selectedIndex.value = -1
    searchInput.value?.focus()
  }

  const handleBlur = () => {
    // Delay hiding results to allow for clicks
    setTimeout(() => {
      showResults.value = false
    }, 200)
  }

  const handleEnter = () => {
    if (selectedIndex.value >= 0 && searchResults.value[selectedIndex.value]) {
      selectResult(searchResults.value[selectedIndex.value])
    } else if (searchResults.value.length > 0) {
      selectResult(searchResults.value[0])
    }
  }

  const navigateResults = (direction: number) => {
    if (searchResults.value.length === 0) return

    const newIndex = selectedIndex.value + direction
    if (newIndex >= 0 && newIndex < searchResults.value.length) {
      selectedIndex.value = newIndex
    } else if (newIndex < 0) {
      selectedIndex.value = searchResults.value.length - 1
    } else {
      selectedIndex.value = 0
    }
  }

  const selectResult = (result: SearchResult) => {
    router.push(`/docs/${result.categorySlug}/${result.sectionSlug}`)
    clearSearch()
  }

  const getContentPreview = (content: string): string => {
    const maxLength = 100
    if (content.length <= maxLength) return content
    return `${content.substring(0, maxLength)  }...`
  }

  const getRelevanceScore = (score: number): number => {
    if (score >= 5) return 3
    if (score >= 3) return 2
    return 1
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) return 'today'
    if (diffInDays === 1) return 'yesterday'
    if (diffInDays < 7) return `${diffInDays} days ago`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`
    return `${Math.floor(diffInDays / 365)} years ago`
  }

  // Watch for search query changes
  watch(searchQuery, () => {
    if (searchQuery.value.trim()) {
      showResults.value = true
    } else {
      showResults.value = false
    }
  })

  // Focus search input when component mounts
  const focusSearch = () => {
    nextTick(() => {
      searchInput.value?.focus()
    })
  }

  // Expose focus method
  defineExpose({
    focus: focusSearch,
  })
</script>

<style scoped>
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Custom scrollbar for search results */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    @apply bg-gray-100;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }

  /* Dark mode scrollbar styles */
  .dark .overflow-y-auto::-webkit-scrollbar-track {
    @apply bg-gray-700;
  }

  .dark .overflow-y-auto::-webkit-scrollbar-thumb {
    @apply bg-gray-500;
  }

  .dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
</style>
